import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, guard } from '../services/auth.api'
import { AuthenticationMethod, CardinalBaseSdk, CardinalSdk, DecryptedDocument, DelegationTag, HealthcareParty, Pkcs8Bytes, User } from '@icure/cardinal-sdk'
import { ICURE_CLOUD_URL } from '../../constants'
import { PatientFilters } from '@icure/cardinal-sdk/filters.mjs'

export const startReplications = (sdk: CardinalSdk) => {
  setInterval(async () => {
    const currentUser = await sdk.user.getCurrentUser()
    const doc = await sdk.document.getDocument(`transfer-ms-${currentUser.id}`).catch(() => null)

    if (doc) {
      const mainAttachment = await sdk.document.getAndDecryptMainAttachment(doc).catch(() => null)
      if (mainAttachment) {
        try {
          const { username, password, dataOwner } = JSON.parse(new TextDecoder().decode(mainAttachment)) as {
            username: string
            password: string
            dataOwner: string
          }
          const otherSdk = await CardinalBaseSdk.initialize(undefined, ICURE_CLOUD_URL, new AuthenticationMethod.UsingCredentials.UsernamePassword(username, password))

          //Fetch by using the token obtained from the sdk
          const fromToken = await sdk.auth.getBearerToken()
          const toToken = await otherSdk.auth.getBearerToken()

          const exchangeData = await fetch(`${ICURE_CLOUD_URL}/rest/v2/exchangedata/byParticipant/${dataOwner}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${fromToken}`,
            },
          }).then((res) => res.json())

          const existingExchangeDataOnDestination = await fetch(`${ICURE_CLOUD_URL}/rest/v2/exchangedata/byParticipant/${dataOwner}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${toToken}`,
            },
          }).then((res) => res.json())

          await Promise.all(
            exchangeData.rows.map(async (ed: any) => {
              if (!existingExchangeDataOnDestination.rows.find((eed: any) => eed.id === ed.id)) {
                delete ed.rev
                await fetch(`${ICURE_CLOUD_URL}/rest/v2/exchangedata`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${toToken}`,
                  },
                  body: JSON.stringify(ed),
                })
              }
            }),
          )

          const patients = await sdk.patient.encrypted.filterPatientsBy(PatientFilters.allPatientsForSelf())
          while (await patients.hasNext()) {
            const patientsBatch = await patients.next(10)
            await Promise.all(
              patientsBatch.map(async (p) => {
                const existingPatientOnDestination = await otherSdk.patient.getPatient(p.id).catch(() => null)
                if (!existingPatientOnDestination) {
                  delete p.rev
                  await otherSdk.patient.createPatient(p)
                }
              }),
            )
          }
        } catch (e) {
          console.error('Failed to replicate from Medispring instance', e)
        }
      }
    }
  }, 60000)
}

export const interopApiRtk = createApi({
  reducerPath: 'interopApi',
  tagTypes: ['Crypto'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    giveAccessToNewHealthcarePartyWithPrivateKey: builder.mutation<
      boolean | undefined,
      {
        username: string
        password: string
      }
    >({
      async queryFn({ username, password }, { getState }) {
        const { healthcareParty: healthcarePartyApi, patient: patientApi, user: userApi, auth: authApi, document: documentApi } = (await cardinalApi(getState)) ?? {}
        return guard([healthcarePartyApi, patientApi, userApi, documentApi], async ([healthcarePartyApi, patientApi, userApi, documentApi]): Promise<boolean> => {
          const currentUser = await userApi.getCurrentUser()
          if (!currentUser) {
            throw new Error('You are not logged in')
          }
          let selfOnMs: HealthcareParty | undefined
          try {
            const otherSdk = await CardinalBaseSdk.initialize(undefined, ICURE_CLOUD_URL, new AuthenticationMethod.UsingCredentials.UsernamePassword(username, password))
            selfOnMs = await otherSdk.healthcareParty.getCurrentHealthcareParty()
          } catch (e) {
            throw new Error('Failed to authenticate with Medispring instance. Please check your credentials.')
          }

          delete selfOnMs.rev

          let selfHere = await healthcarePartyApi.getHealthcareParty(selfOnMs.id).catch(() => null)
          if (!selfHere) {
            selfHere = await healthcarePartyApi.createHealthcareParty(selfOnMs)
          }

          if (!selfHere) {
            throw new Error('Failed to create or retrieve healthcare party')
          }

          const doc = await documentApi.getDocument(`transfer-ms-${currentUser.id}`).catch(() => null)
          if (!doc) {
            const newDoc = await documentApi.createDocument(
              await documentApi.withEncryptionMetadataUnlinked(
                new DecryptedDocument({
                  id: `transfer-ms-${currentUser.id}`,
                }),
              ),
            )
            if (!newDoc) {
              throw new Error('Failed to create user document')
            }
            await documentApi.encryptAndSetMainAttachment(
              newDoc,
              ['public.json'],
              new Int8Array(new TextEncoder().encode(JSON.stringify({ username, password, dataOwner: selfOnMs.id }))),
            )
          } else {
            await documentApi.encryptAndSetMainAttachment(
              doc,
              ['public.json'],
              new Int8Array(new TextEncoder().encode(JSON.stringify({ username, password, dataOwner: selfOnMs.id }))),
            )
          }

          const patients = await patientApi.filterPatientsBy(PatientFilters.allPatientsForSelf())
          while (await patients.hasNext()) {
            await Promise.all(
              (await patients.next(10)).map(async (p) => {
                await patientApi?.shareWith(selfHere.id, p)
              }),
            )
          }

          const me = await userApi.getCurrentUser()
          await userApi.modifyUser(
            new User({
              ...me,
              autoDelegations: {
                ...me.autoDelegations,
                [DelegationTag.All]: (me.autoDelegations[DelegationTag.All as 'All'] ?? []).filter((x) => x !== selfOnMs.id).concat(selfOnMs.id),
              },
            }),
          )

          return true
        })
      },
    }),
  }),
})

export const { useGiveAccessToNewHealthcarePartyWithPrivateKeyMutation } = interopApiRtk
